import { ReactNode } from 'react'
import { useRequirePremium } from '@/hooks/useRequirePremium'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Loader2, Crown, Calendar } from 'lucide-react'

interface PremiumGateProps {
  children: ReactNode
  bypassForAdmin?: boolean
  showTrialBanner?: boolean
}

/**
 * Component that gates content behind premium subscription
 * Shows trial status and redirects after 30 days
 */
export function PremiumGate({ 
  children, 
  bypassForAdmin = true,
  showTrialBanner = true 
}: PremiumGateProps) {
  const { 
    isTrialActive, 
    daysRemaining, 
    needsUpgrade, 
    loading,
    checkoutUrl 
  } = useRequirePremium({ 
    redirectToCheckout: true,
    bypassForAdmin 
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  // Show upgrade required screen (shouldn't normally show because of redirect)
  if (needsUpgrade) {
    return (
      <div className="container mx-auto max-w-2xl py-12">
        <Card className="border-2 border-yellow-500/50">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Crown className="h-16 w-16 text-yellow-500" />
            </div>
            <CardTitle className="text-3xl">Upgrade to Premium</CardTitle>
            <CardDescription className="text-lg">
              Your 30-day trial has ended. Upgrade now to continue using SleepNest.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center">
              <p className="text-muted-foreground mb-6">
                Get unlimited access to all features including:
              </p>
              <ul className="text-left space-y-2 mb-6 max-w-md mx-auto">
                <li>✅ Unlimited sleep tracking</li>
                <li>✅ AI-powered insights</li>
                <li>✅ Expert sleep coaching</li>
                <li>✅ Advanced analytics</li>
                <li>✅ Priority support</li>
              </ul>
            </div>
            <div className="flex justify-center">
              <Button 
                size="lg" 
                className="bg-yellow-500 hover:bg-yellow-600 text-black"
                onClick={() => window.location.href = checkoutUrl}
              >
                <Crown className="mr-2 h-5 w-5" />
                Upgrade to Premium
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Show trial banner if in trial period
  if (showTrialBanner && isTrialActive && daysRemaining <= 7) {
    return (
      <>
        <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-b border-yellow-500/20 py-3">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-yellow-500" />
              <span className="text-sm font-medium">
                {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining in your trial
              </span>
            </div>
            <Button 
              size="sm" 
              variant="outline"
              className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black"
              onClick={() => window.location.href = checkoutUrl}
            >
              <Crown className="mr-2 h-4 w-4" />
              Upgrade Now
            </Button>
          </div>
        </div>
        {children}
      </>
    )
  }

  // All good - show content
  return <>{children}</>
}
